import { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { createColumnHelper } from '@tanstack/react-table';
import { Card, Form, Button, Spinner, Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { settlementSchema } from '../../../schemas/wallet/admin.schema';
import DataTable from '../../../component/wallet/data-table/DataTable';
import DataTableToolbar from '../../../component/wallet/data-table/DataTableToolbar';
import StatusBadge from '../../../component/wallet/StatusBadge';
import CurrencyDisplay from '../../../component/wallet/CurrencyDisplay';
import ConfirmDialog from '../../../component/wallet/ConfirmDialog';
import { useAdmin } from '../../../Hooks/wallet/useadmin';
import { formatDate } from '../../../lib/wallet/utils';
import { SETTLEMENT_STATUSES, DEFAULT_PAGE_SIZE } from '../../../lib/wallet/constants';
import { getCurrencies } from '../../../api/wallet/wallet.api';

const columnHelper = createColumnHelper();

const SettlementsPage = () => {
    const { sidebar } = useContext(AppContext);
    const { loading, doGenerateSettlement, fetchSettlements, doConfirmSettlement, fetchApiKeys } = useAdmin();
    const [settlements, setSettlements] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [apiKeys, setApiKeys] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: DEFAULT_PAGE_SIZE, total_count: 0, total_pages: 1 });

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        resolver: zodResolver(settlementSchema),
    });

    useEffect(() => {
        const load = async () => {
            try {
                const [currRes, keysRes] = await Promise.all([
                    getCurrencies(),
                    fetchApiKeys({ limit: 100 }),
                ]);
                setCurrencies(currRes?.data || []);
                setApiKeys(keysRes?.data?.api_keys || []);
            } catch {
                // handled
            }
        };
        load();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadSettlements = useCallback(
        async (page = 1) => {
            try {
                const res = await fetchSettlements({
                    page,
                    limit: pagination.limit,
                    status: statusFilter || undefined,
                });
                setSettlements(res?.data?.settlements || []);
                setPagination((prev) => ({ ...prev, page, ...res?.data?.pagination }));
            } catch {
                // handled
            }
        },
        [pagination.limit, statusFilter, fetchSettlements]
    );

    useEffect(() => {
        loadSettlements(1);
    }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const onGenerate = async (data) => {
        try {
            await doGenerateSettlement(data);
            reset();
            loadSettlements(1);
        } catch {
            // handled
        }
    };

    const handleConfirm = async (id) => {
        const confirmed = await ConfirmDialog({
            title: 'Confirm Settlement',
            message: `Confirm settlement #${id}?`,
        });
        if (!confirmed) return;
        try {
            await doConfirmSettlement(id);
            loadSettlements(pagination.page);
        } catch {
            // handled
        }
    };

    const columns = useMemo(
        () => [
            columnHelper.accessor('id', { header: 'ID', size: 60 }),
            columnHelper.accessor('app_source', { header: 'App Source' }),
            columnHelper.accessor('currency_code', { header: 'Currency' }),
            columnHelper.display({
                id: 'period',
                header: 'Period',
                cell: ({ row }) =>
                    `${formatDate(row.original.period_start, 'MMM dd, yyyy')} — ${formatDate(row.original.period_end, 'MMM dd, yyyy')}`,
            }),
            columnHelper.accessor('total_debits', {
                header: 'Debits',
                cell: (info) => <CurrencyDisplay amount={info.getValue()} currencySymbol={info.row.original.currency_symbol} />,
            }),
            columnHelper.accessor('total_credits', {
                header: 'Credits',
                cell: (info) => <CurrencyDisplay amount={info.getValue()} currencySymbol={info.row.original.currency_symbol} />,
            }),
            columnHelper.accessor('net_revenue', {
                header: 'Net Revenue',
                cell: (info) => <CurrencyDisplay amount={info.getValue()} currencySymbol={info.row.original.currency_symbol} />,
            }),
            columnHelper.accessor('transaction_count', { header: 'Txns' }),
            columnHelper.accessor('status', {
                header: 'Status',
                cell: (info) => <StatusBadge status={info.getValue()} />,
            }),
            columnHelper.display({
                id: 'actions',
                header: '',
                cell: ({ row }) =>
                    row.original.status === 'pending' ? (
                        <Button size="sm" variant="outline-success" onClick={() => handleConfirm(row.original.id)}>
                            Confirm
                        </Button>
                    ) : null,
            }),
        ],
        [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    return (
        <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
            <div className="role-content-main">
            <div className="sub-header-role mb-4">
                <h6 className="mt-2 role-text">Settlements</h6>
            </div>
                <div className="fade-in">
            

            {/* Generate Settlement Form */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-transparent fw-semibold">Generate Settlement Report</Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit(onGenerate)}>
                        <Row className="g-3">
                            <Col xs={12} md={3}>
                                <Form.Group>
                                    <Form.Label>App Source</Form.Label>
                                    <Form.Select isInvalid={!!errors.app_source} {...register('app_source')}>
                                        <option value="">Select App</option>
                                        {apiKeys.map((k) => (
                                            <option key={k.id} value={k.id}>
                                                {k.app_name || `Key #${k.id}`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{errors.app_source?.message}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={2}>
                                <Form.Group>
                                    <Form.Label>Currency</Form.Label>
                                    <Form.Select isInvalid={!!errors.currency_code} {...register('currency_code')}>
                                        <option value="">Currency</option>
                                        {currencies.map((c) => (
                                            <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{errors.currency_code?.message}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={3}>
                                <Form.Group>
                                    <Form.Label>Period Start</Form.Label>
                                    <Form.Control type="date" isInvalid={!!errors.period_start} {...register('period_start')} />
                                    <Form.Control.Feedback type="invalid">{errors.period_start?.message}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={3}>
                                <Form.Group>
                                    <Form.Label>Period End</Form.Label>
                                    <Form.Control type="date" isInvalid={!!errors.period_end} {...register('period_end')} />
                                    <Form.Control.Feedback type="invalid">{errors.period_end?.message}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={1} className="d-flex align-items-end">
                                <Button type="submit" variant="primary" disabled={loading} className="w-100">
                                    {loading ? <Spinner size="sm" /> : 'Generate'}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Settlements List */}
            <DataTableToolbar
                filters={[
                    { value: statusFilter, onChange: setStatusFilter, placeholder: 'All Statuses', options: SETTLEMENT_STATUSES },
                ]}
            />

            <DataTable
                columns={columns}
                data={settlements}
                loading={loading}
                page={pagination.page}
                pageSize={pagination.limit}
                totalCount={pagination.total_count}
                totalPages={pagination.total_pages}
                onPageChange={(p) => loadSettlements(p)}
                onPageSizeChange={(s) => setPagination((prev) => ({ ...prev, limit: s }))}
                emptyTitle="No settlements"
                emptyDescription="Generate a settlement report to get started."
            />
        </div>
        </div>
        </div>
    );
};

export default SettlementsPage;
